import * as React from 'react';
import { AlertTriangle, Inbox, Lock, RefreshCw, WifiOff } from 'lucide-react';
import { ErroHttp } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Botao } from './botao';

export function EstadoVazio({
  titulo,
  detalhe,
  icone,
  acao,
  className,
}: {
  titulo: string;
  detalhe?: string | undefined;
  icone?: React.ReactNode | undefined;
  acao?: React.ReactNode | undefined;
  className?: string | undefined;
}): JSX.Element {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 px-6 py-14 text-center',
        className,
      )}
    >
      {/* Bolha com anel duplo: dá peso visual ao vazio sem virar ilustração
          genérica de estoque. */}
      <span className="relative grid h-16 w-16 place-items-center rounded-2xl bg-superficie2 text-textoSuave ring-1 ring-inset ring-borda">
        <span
          aria-hidden
          className="absolute inset-0 rounded-2xl ring-8 ring-superficie2/60"
          style={{ margin: -8 }}
        />
        <span className="relative">{icone ?? <Inbox className="h-7 w-7" />}</span>
      </span>
      <div className="space-y-1">
        <p className="text-[14.5px] font-semibold text-texto">{titulo}</p>
        {detalhe ? (
          <p className="mx-auto max-w-md text-[13px] leading-relaxed text-textoFraco">{detalhe}</p>
        ) : null}
      </div>
      {acao ? <div className="mt-1">{acao}</div> : null}
    </div>
  );
}

/**
 * Estado de erro por status: 403 não é falha do usuário nem coisa para
 * "tentar de novo"; rede e 503 são. Tratar tudo como "erro genérico"
 * é o que faz o operador chamar o TI sem necessidade.
 */
export function EstadoErro({
  erro,
  aoTentarDeNovo,
  className,
}: {
  erro: unknown;
  aoTentarDeNovo?: (() => void) | undefined;
  className?: string | undefined;
}): JSX.Element {
  const http = erro instanceof ErroHttp ? erro : null;
  const semPermissao = http?.semPermissao ?? false;
  const semRede = http?.status === 0;
  const indisponivel = http?.status === 503;

  const titulo = semPermissao
    ? 'Você não tem permissão para ver isto'
    : semRede
      ? 'Sem conexão com o servidor'
      : indisponivel
        ? 'Servidor temporariamente indisponível'
        : 'Não foi possível carregar';

  const icone = semPermissao ? (
    <Lock className="h-7 w-7" />
  ) : semRede ? (
    <WifiOff className="h-7 w-7" />
  ) : (
    <AlertTriangle className="h-7 w-7" />
  );

  const mensagem =
    http?.message ?? (erro instanceof Error ? erro.message : 'Erro inesperado na aplicação.');

  return (
    <EstadoVazio
      className={className}
      titulo={titulo}
      detalhe={mensagem}
      icone={<span className="text-status-divergencia">{icone}</span>}
      acao={
        aoTentarDeNovo && !semPermissao ? (
          <Botao variante="secundaria" tamanho="pequeno" onClick={aoTentarDeNovo}>
            <RefreshCw className="h-4 w-4" />
            Tentar de novo
          </Botao>
        ) : null
      }
    />
  );
}

/** Aviso de limitação real: API que ainda não existe, dado que não dá
 *  para afirmar. Nunca acompanhado de um dado inventado para compensar. */
export function Aviso({
  titulo,
  detalhe,
  className,
}: {
  titulo: string;
  detalhe: string;
  className?: string | undefined;
}): JSX.Element {
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-xl border border-status-atencao/30 bg-status-atencaoFundo px-4 py-3.5 shadow-xs',
        className,
      )}
    >
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-status-atencao/15 text-status-atencao">
        <AlertTriangle className="h-4 w-4" />
      </span>
      <div className="text-[13px] leading-relaxed">
        <p className="font-semibold text-texto">{titulo}</p>
        <p className="text-textoFraco">{detalhe}</p>
      </div>
    </div>
  );
}
